import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { BackToTop } from "@/components/feature/BackToTop";
import { siteInfo, stats } from "@/mocks/articles";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";
import ContactForm from "./components/ContactForm";

const PRINCIPLES = [
  { icon: "ri-search-eye-line", title: "先查证，再下笔", desc: "任何进入正文的结论，都需要有可公开定位的原始出处支撑。" },
  { icon: "ri-scales-3-line", title: "区分事实与推测", desc: "已确认的事实、合理的推测、待验证的说法，在文中会明确分开表述。" },
  { icon: "ri-user-heart-line", title: "不做人身攻击", desc: "我们讨论的是事件逻辑与行业结构，而不是给任何个体定性。" },
  { icon: "ri-refresh-line", title: "错了就改", desc: "发现事实性错误会公开更正，并在原文标注修改说明。" },
];

const MILESTONES = [
  { year: "2021", text: "几位内容行业从业者开始用业余时间整理事件脉络，最初的复盘只发给身边的朋友。" },
  { year: "2022", text: "内容正式栏目化，确立文娱观察、网络热点等固定方向，形成统一的写作规范。" },
  { year: "2023", text: "推出每日瓜料简报，把当天的公开讨论整理成一封短邮件，订阅人数稳步增长。" },
  { year: "2025", text: "将「事件时间线」确立为每篇复盘的固定结构，读者可以一眼看清关键节点。" },
  { year: "2026", text: "站点整体改版，强化来源标注与更正机制，让内容更经得起反复阅读。" },
];

const ABOUT_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于我们｜看料吃瓜",
      url: `${SITE_ORIGIN}/about`,
      inLanguage: "zh-CN",
      description: "了解看料吃瓜的定位、内容原则与发展历程，以及我们的联系与投稿方式。",
    },
    {
      "@type": "Organization",
      name: siteInfo.name,
      url: `${SITE_ORIGIN}/`,
      email: siteInfo.email,
      foundingDate: siteInfo.since,
      description: siteInfo.intro,
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: siteInfo.email,
        availableLanguage: "zh-CN",
      },
    },
    {
      "@type": "ContactPage",
      name: "联系我们｜看料吃瓜",
      url: `${SITE_ORIGIN}/about#contact`,
      inLanguage: "zh-CN",
    },
  ],
};

export default function About() {
  usePageMeta({
    title: "关于我们｜看料吃瓜的定位、内容原则与联系方式",
    description:
      "看料吃瓜是一个专注文娱与全网热点深度复盘的内容站点。这里介绍我们的定位、内容原则、发展历程，以及纠错、投稿与合作的联系方式。",
    path: "/about",
    keywords: "看料吃瓜,关于看料吃瓜,内容原则,投稿联系",
    type: "website",
    jsonLd: ABOUT_JSON_LD,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
          <div className="mx-auto flex max-w-editorial items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">关于我们</span>
          </div>
        </nav>

        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-editorial px-4 py-12 md:px-6 md:py-16">
            <p className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              <span className="inline-block h-3 w-0.5 bg-primary-500" aria-hidden="true"></span>
              关于我们
            </p>
            <h1 className="max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[42px]">
              我们只做一件事：把热点讲清楚
            </h1>
            <p className="mt-5 max-w-3xl text-sm leading-loose text-foreground-600 md:text-base">{siteInfo.intro}</p>

            <dl className="mt-9 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-background-300 pt-7 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label}>
                  <dt className="text-xs leading-relaxed text-foreground-500">{item.label}</dt>
                  <dd className="mt-1.5 font-heading text-2xl font-bold text-foreground-950">
                    {item.value}
                    <span className="ml-0.5 text-xs font-normal text-foreground-500">{item.unit}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </header>

        <section className="mx-auto max-w-editorial px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div className="h-[260px] overflow-hidden rounded-lg bg-background-200 md:h-[380px]">
              <img
                src="https://readdy.ai/api/search-image?query=editorial%20photography%20of%20an%20organized%20research%20desk%20with%20notebooks%20laptop%20and%20printed%20timeline%20cards%20against%20a%20warm%20cream%20wall%2C%20soft%20natural%20light%2C%20terracotta%20and%20sage%20accents%2C%20clean%20minimal%20composition&width=1400&height=1000&seq=about-desk-j1&orientation=landscape"
                alt="看料吃瓜编辑部整理事件时间线的工作台"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[30px]">
                我们相信，读者需要的不是情绪，而是逻辑
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-600 md:text-[15px]">
                <p>
                  网络热点往往在几小时内完成从发生到刷屏的全过程。信息被反复转述、剪辑、放大，最后留下来的多数是情绪，而不是事实本身。
                </p>
                <p>
                  看料吃瓜想做的，是把这些被压缩掉的部分补回来：这件事最早从哪来，中间经历了哪些节点，各方说了什么，产业规则又在其中起了什么作用。
                </p>
                <p>
                  我们不承诺给你一个唯一的答案，但会尽量把判断所需的材料摆在你面前。剩下那一步，交给你自己走。
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  自 {siteInfo.since} 年持续更新
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-800">
                  <i className="ri-team-line" aria-hidden="true"></i>
                  {siteInfo.editors.length} 位常驻作者
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto max-w-editorial px-4 py-14 md:px-6 md:py-20">
            <div className="max-w-2xl">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                <span className="inline-block h-3 w-0.5 bg-primary-500" aria-hidden="true"></span>
                内容原则
              </p>
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[30px]">
                四条不轻易让步的底线
              </h2>
            </div>
            <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {PRINCIPLES.map((item, index) => (
                <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      <span className="mr-2 font-label text-xs font-normal text-foreground-400">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-editorial px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-4">
              <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[30px]">
                我们是怎样走到今天的
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-600">
                从几个人的业余整理，到现在固定的写作规范与更正机制，变的是流程，没变的是那句最初的出发点。
              </p>
            </div>
            <div className="lg:col-span-8">
              <ol className="relative space-y-8 border-l border-background-300 pl-6">
                {MILESTONES.map((item) => (
                  <li key={item.year} className="relative">
                    <span className="absolute -left-[31px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background-50 bg-accent-500" aria-hidden="true"></span>
                    <p className="font-heading text-lg font-bold text-primary-600">{item.year}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section id="contact" className="scroll-mt-24 border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-editorial px-4 py-14 md:px-6 md:py-20">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-5">
                <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
                  <span className="inline-block h-3 w-0.5 bg-primary-500" aria-hidden="true"></span>
                  联系我们
                </p>
                <h2 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[30px]">
                  纠错、线索与合作，都可以从这里开始
                </h2>
                <p className="mt-4 text-sm leading-loose text-foreground-600">
                  我们最欢迎的是带有原始出处的线索：完整截图、原始链接与时间信息。仅凭转述的内容，我们无法进入核实流程。
                </p>

                <ul className="mt-7 space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-mail-line" aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">读者来信与合作</p>
                      <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-900 transition-colors hover:text-primary-600">
                        {siteInfo.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-inbox-archive-line" aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">爆料与投稿</p>
                      <a href={`mailto:${siteInfo.tipEmail}`} className="text-sm text-foreground-900 transition-colors hover:text-primary-600">
                        {siteInfo.tipEmail}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className="ri-time-line" aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">回复时间</p>
                      <p className="text-sm text-foreground-900">工作日 10:00 – 19:00，两个工作日内回复</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="lg:col-span-7">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}